import { ANIMATION_TARGET_ID, isDebug } from "@/constants";
import type { Animation } from "@/types";
import { Bone, PropertyBinding, type Object3D, type SkinnedMesh } from "three";

const skippedSkinnedMeshTargets: Record<string, string[]> = {
  // LEGO - d4 tiles
  "05901": [
    "TileNever1Mesh",
    "TileNever2Mesh",
    "TileNever3Mesh",
    "TileNever4Mesh",
  ],
  // Planar Summoning - creatures
  "03001": [
    "astralDreadnoughtMesh",
    "marutMesh",
    "nightwalkerMesh",
    "kirinMesh",
    "flailSnailMesh",
    "elderBrainMesh",
  ],
  // Clash of Thorns
  "04101": [
    // Knight helmet
    "Mesh029",
    // Knight glyph
    "Mesh029_1",
    // Soth helmet
    "Mesh",
    // Soth glyph
    "Mesh_1",
    // Crit roses
    "RoseMeshBotL",
    "RoseMeshMidL",
    "RoseMeshUpL",
    "RoseMeshBotR",
    "RoseMeshMidR",
    "RoseMeshUpR",
  ],
};

// Normalize the animation targetId:
// "Clone of dice_primitive0.dice_primitive0" -> "dice_primitive0"
// "Clone of Triangle.002_primitive0.Triangle.002_primitive0" -> "Triangle.002_primitive0"
export const normalizeTargetId = (targetId: string) => {
  const s = targetId.trim().replace(/^Clone of\s+/i, "");
  // try to find two identical halves and return just one
  const m = s.match(/^(.+)\.\1$/);
  // otherwise return the entire target id
  return m ? m[1] : s;
};

// Find the object in the scene with a matching userData animationTargetId
// @see setUserDataAnimationTargets.ts
export const getAnimationTarget = (
  setId: string,
  root: Object3D,
  targetId: string,
  animation: Animation,
) => {
  if (!root) return null;

  let target: Object3D | null = null;
  const key = normalizeTargetId(targetId);
  root.traverse((o: Object3D) => {
    if (!target && o.userData?.[ANIMATION_TARGET_ID]?.includes?.(key)) {
      target = o;
    }
  });

  if (!target) {
    if (isDebug) {
      console.warn(
        `Could not find target for animation on ${root.userData.name}: ${targetId}`,
      );
    }

    return null;
  }

  // If we're dealing with a SkinnedMesh and scaling (only one we know of so far)
  // we need to apply the scaling to the root bone instead of the mesh itself
  const skinnedMesh = target as SkinnedMesh;
  if (
    skinnedMesh.isSkinnedMesh &&
    animation.property === "scaling" &&
    !skippedSkinnedMeshTargets[setId]?.includes(skinnedMesh.name)
  ) {
    const rootBone = skinnedMesh.skeleton.bones.find(
      (b) => !(b.parent && (b.parent as Bone)?.isBone),
    );
    target = rootBone || target;
  }

  const sanitizedName = PropertyBinding.sanitizeNodeName(target!.name);

  if (isDebug)
    console.log(
      `Resolved target for animation ${animation.name} on ${root.userData.name} (${targetId}): ${sanitizedName}`,
    );

  return sanitizedName;
};
